import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigationContainerRef, usePathname, useRouter, useSegments } from 'expo-router';
import { Platform } from 'react-native';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { ApiService } from '@/services/ApiService';
import { AccountService } from '@/services/domain/AccountService';
import { CommunityCode, communities, getCommunityFromDomain } from '@/constants/communities';
import { resetAppNavigationTree } from '@/libs/navigation/resetAppNavigationTree';
import StorageService from '@/services/StorageService';
import { AccountStatus } from '@/models/AccountStatus';
import { useQueryClient } from '@tanstack/react-query';

// Types from AccountService
export interface Credentials {
  UserName: string;
  MemberId?: number;
  AuthToken?: string;
}

export type CommunityCredentials = Record<CommunityCode, Credentials | undefined>;

interface StoredAuthData {
  credentials: CommunityCredentials;
  currentCommunity?: CommunityCode;
  lastUsedCommunity?: CommunityCode;
}

const AUTH_STORAGE_KEY = 'authData';

/**
 * Device push token, or null when unavailable (web/simulator). No permission
 * check: tokens outlive a permission revocation, and revoked-permission users
 * still need their server-side registration removed.
 */
const getDevicePushToken = async (): Promise<string | null> => {
  if (!Device.isDevice) return null;
  const token = await Notifications.getDevicePushTokenAsync();
  return (token.data as string) ?? null;
};

/** Best-effort removal of this device's push token from a community's server. */
const unregisterPushToken = async (community: CommunityCode, authToken: string): Promise<void> => {
  try {
    const pushToken = await getDevicePushToken();
    if (pushToken) {
      await AccountService.deletePushToken(community, authToken, pushToken);
    }
  } catch (error) {
    console.warn('Failed to remove push token for community:', community, error);
  }
};

// Helper to update ApiService with current credentials
const updateApiServiceCredentials = (
  creds: CommunityCredentials,
  community?: CommunityCode
): void => {
  if (community && creds[community]) {
    ApiService.configure(
      community,
      creds[community]?.UserName,
      creds[community]?.AuthToken,
      Platform.OS
    );
  } else {
    ApiService.configure(undefined, undefined, undefined, Platform.OS);
  }
};

const getCurrentRedirectPath = (pathname: string): string | undefined => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const { pathname: browserPathname, search, hash } = window.location;
    const redirectPath = `${browserPathname}${search}${hash}`;
    return redirectPath === '/' ? undefined : redirectPath;
  }

  return pathname === '/' ? undefined : pathname;
};

type SignInParams = {
  community: CommunityCode;
  username: string;
  password: string;
  rememberMe?: boolean;
};

interface AuthContextProps {
  isAuthenticated: boolean;
  signIn: (params: SignInParams) => Promise<void>;
  signOut: () => void;
  signOutSoft: () => void;
  forget: (community: CommunityCode, options?: { force?: boolean }) => Promise<void>;
  switchCommunity: (community: CommunityCode) => Promise<void>;
  currentCommunity: CommunityCode | undefined;
  lastUsedCommunity: CommunityCode | undefined;
  getCredentials: (community: CommunityCode) => Credentials | null;
  accountStatus: AccountStatus | null;
  isLoading: boolean; // Add isLoading state
  isAuthenticating: boolean; // Network auth check in progress
  updateAccountIconUrl: (iconUrl: string) => void;
  /** Persist-only update of stored username for a community (no network). */
  updateStoredUsername: (community: CommunityCode, newUsername: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [communityCredentials, setCommunityCredentials] = useState<CommunityCredentials>(
    {} as CommunityCredentials
  );
  const [currentCommunity, setCurrentCommunity] = useState<CommunityCode | undefined>(undefined);
  const [lastUsedCommunity, setLastUsedCommunity] = useState<CommunityCode | undefined>(undefined);
  const [communityAuthStatus, setCommunityAuthStatus] = useState<Record<CommunityCode, boolean>>(
    {} as Record<CommunityCode, boolean>
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isManualSignInInProgress, setIsManualSignInInProgress] = useState(false);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null); // Add state for account status
  const [invalidCredentialsCommunity, setInvalidCredentialsCommunity] = useState<CommunityCode | undefined>(undefined);

  const router = useRouter();
  const segments = useSegments();
  const pathname = usePathname();
  const navigationContainerRef = useNavigationContainerRef();

  // Helper method to check if current community is authenticated
  const isCurrentCommunityAuthenticated = useCallback((): boolean => {
    return !!currentCommunity && !!communityAuthStatus[currentCommunity];
  }, [currentCommunity, communityAuthStatus]);

  // Select community and load credentials early in the component lifecycle
  useEffect(() => {
    const initAuth = async () => {
      await ApiService.loadStoredHostPrefix();
      const { hasStoredData, validCurrentCommunity } = await loadAuthData();

      if (!hasStoredData) {
        const domainCommunity = getCommunityFromDomain();
        if (domainCommunity) {
          setLastUsedCommunity(domainCommunity);
        }
        setIsLoading(false);
        return;
      }

      if (validCurrentCommunity) {
        try {
          setIsAuthenticating(true);
          await checkAuthForCommunity(validCurrentCommunity, { force: true });
        } finally {
          setIsAuthenticating(false);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const loadAuthData = async (): Promise<{ hasStoredData: boolean; validCurrentCommunity?: CommunityCode }> => {
    try {
      const storedData = await StorageService.getItem(AUTH_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData) as StoredAuthData;
        setCommunityCredentials(parsedData.credentials);

        // Validate current community exists in communities object
        let validCurrentCommunity =
          parsedData.currentCommunity && communities[parsedData.currentCommunity]
            ? parsedData.currentCommunity
            : undefined;
        
        // Validate last used community exists in communities object
        const lastUsed = parsedData.lastUsedCommunity || parsedData.currentCommunity;
        const validLastUsedCommunity = lastUsed && communities[lastUsed] ? lastUsed : undefined;

        // Only set if valid
        setCurrentCommunity(validCurrentCommunity);
        setLastUsedCommunity(validLastUsedCommunity);

        if (validCurrentCommunity) {
          updateApiServiceCredentials(parsedData.credentials, validCurrentCommunity);
        }

        return { hasStoredData: true, validCurrentCommunity };
      }

      return { hasStoredData: false };
    } catch (error) {
      console.error('Failed to load auth data:', error);
      setCommunityCredentials({} as CommunityCredentials);
      return { hasStoredData: false };
    }
  };

  const saveAuthData = useCallback(async (
    newCredentials: CommunityCredentials,
    newCurrentCommunity?: CommunityCode,
    newLastUsedCommunity?: CommunityCode
  ) => {
    try {
      const lastUsed = newLastUsedCommunity || newCurrentCommunity || lastUsedCommunity;

      const dataToStore: StoredAuthData = {
        credentials: newCredentials,
        currentCommunity: newCurrentCommunity,
        lastUsedCommunity: lastUsed,
      };

      setLastUsedCommunity(lastUsed);

      await StorageService.setItem(AUTH_STORAGE_KEY, JSON.stringify(dataToStore));
      updateApiServiceCredentials(newCredentials, newCurrentCommunity);
    } catch (error) {
      console.error('Failed to save auth data:', error);
    }
  }, [lastUsedCommunity]);

  // Effect for auth checking
  useEffect(() => {
    if (isLoading || isAuthenticating) return;

    const checkAuthStatus = async () => {
      const protectedPrefixes = ['/(app)/'];
      const currentRoute = `/${segments.join('/')}`;
      const isProtected = protectedPrefixes.some((prefix) => currentRoute.startsWith(prefix)) || currentRoute === '/';
      
      if (isProtected && !isManualSignInInProgress) {
        const userIsAuthenticated = await checkAuth();
        if (!userIsAuthenticated) {
          const redirectPath = getCurrentRedirectPath(pathname);

          if (Platform.OS === 'web') {
            router.replace(
              (redirectPath ? `/sign-in?redirect=${encodeURIComponent(redirectPath)}` : '/sign-in') as any
            );
          } else if (lastUsedCommunity && invalidCredentialsCommunity === lastUsedCommunity) {
            router.replace({ pathname: '/communities' });
            setTimeout(() => {
              router.push({
                pathname: '/communities/[community]/sign-in',
                params: {
                  community: lastUsedCommunity,
                  redirect: redirectPath,
                },
              });
            }, 200);
          } else {
            // No community context, redirect to communities list
            router.replace('/communities');
          }
        }
      }
    };

    checkAuthStatus();
  }, [segments, pathname, isLoading, isAuthenticating, currentCommunity, isManualSignInInProgress]);

  const checkAuthForCommunity = useCallback(async (
    community: CommunityCode,
    options?: { force?: boolean }
  ): Promise<boolean> => {
    if (isAuthenticating && !options?.force) return !!communityAuthStatus[community]; // prevent concurrent checks
    
    // Return early if already authenticated unless a refresh is explicitly requested
    if (communityAuthStatus[community] && !options?.force) {
      return true;
    }

    const creds = communityCredentials[community];
    const apiCommunity = ApiService.getCurrentCommunity?.();
    if (!creds && apiCommunity !== community) {
      console.error('No credentials found for:', community);
      return false;
    }

    // Soft authentication check; only treat 401 as unauthenticated
    setIsAuthenticating(true);
    try {
      const status = await AccountService.checkIn();
      setAccountStatus(status);
      const newAuthStatus = { ...communityAuthStatus, [community]: true };
      setCommunityAuthStatus(newAuthStatus);
      if (invalidCredentialsCommunity === community) {
        setInvalidCredentialsCommunity(undefined);
      }
      return true;
    } catch (error: any) {
      if (error?.status === 401) {
        console.error('Authentication check failed:', error);
        setCurrentCommunity(undefined);
        setInvalidCredentialsCommunity(community);
        return false;
      }
      // For other errors (403, network issues, etc.), if we have credentials,
      // pretend the user is authenticated and let upstream components handle the error
      console.error('Error during API check in:', error);
      if (creds) {
        const newAuthStatus = { ...communityAuthStatus, [community]: true };
        setCommunityAuthStatus(newAuthStatus);
      }
      return true;
    } finally {
      setIsAuthenticating(false);
    }
  }, [isAuthenticating, communityAuthStatus, communityCredentials, invalidCredentialsCommunity]);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    if (!currentCommunity) return false;
    return checkAuthForCommunity(currentCommunity);
  }, [currentCommunity, checkAuthForCommunity]);

  const signIn = useCallback(async (params: SignInParams): Promise<void> => {
    const previousCommunity = currentCommunity;
    try {
      setIsManualSignInInProgress(true);

      const { community, username, password, rememberMe } = params;

      await queryClient.cancelQueries();

      const newCredentials = { ...communityCredentials };
      newCredentials[community] = { UserName: username };

      setCommunityCredentials(newCredentials);

      ApiService.configure(community, username, undefined, Platform.OS);

      const accountStatus = await AccountService.signIn({
        Username: username,
        Password: password,
        RememberMe: rememberMe ?? false,
      });

      setAccountStatus(accountStatus);

      // Update credentials with MemberId and AuthToken if available
      if (newCredentials[community]) {
        const updatedCreds = {
          ...newCredentials,
          [community]: {
            UserName: username,
            MemberId: accountStatus.MemberId,
            AuthToken: accountStatus.AuthToken, // This will be undefined for web platform
          },
        };

        // Set auth status to true after successful sign in
        const newAuthStatus = { ...communityAuthStatus, [community]: true };
        setCommunityAuthStatus(newAuthStatus);

        setCommunityCredentials(updatedCreds);
        setLastUsedCommunity(community);
        setCurrentCommunity(community);

        await saveAuthData(updatedCreds, community);

        // Clear cached data only after ApiService has the final credentials. Otherwise mounted
        // tab queries can refetch during the half-signed-in window and cache unauthorized errors.
        queryClient.clear();

        // Signing in from the directory while another community's app tree
        // is still mounted (browse-communities keeps the user signed in):
        // reset it like switchCommunity does, or its tabs keep the previous
        // community's screens. No-op on the classic signed-out path.
        if (previousCommunity && previousCommunity !== community) {
          resetAppNavigationTree(navigationContainerRef);
        }
      }
    } catch (error) {
      console.error('Sign in failed:', error);
      throw error;
    } finally {
      setIsManualSignInInProgress(false);
    }
  }, [queryClient, communityCredentials, communityAuthStatus, saveAuthData, currentCommunity, navigationContainerRef]);

  const _performSignOut = useCallback(async (softSignOut: boolean = false) => {
    if (Platform.OS === 'web') {
      router.replace('/sign-in');
    } else {
      router.replace('/communities');
    }

    // Clear all TanStack Query caches to prevent data leakage between users
    queryClient.clear();

    try {
      if (currentCommunity) {
        if (!softSignOut) {
          await AccountService.signOut();
        }

        const newAuthStatus = { ...communityAuthStatus };
        delete newAuthStatus[currentCommunity];
        setCommunityAuthStatus(newAuthStatus);
      }
    } catch (error) {
      console.error('Error during API sign out:', error);
      // Continue with local sign out even if API call fails
    } finally {
      // Clear any in-memory account status to avoid showing stale data after sign-out
      setAccountStatus(null);
      setInvalidCredentialsCommunity(undefined);
      updateApiServiceCredentials(communityCredentials);
      // Clear lastUsedCommunity so the next cold start lands on /communities
      setLastUsedCommunity(undefined);
      await saveAuthData(communityCredentials, undefined, undefined);
      setCurrentCommunity(undefined);
    }
  }, [router, queryClient, currentCommunity, communityAuthStatus, communityCredentials, saveAuthData]);

  const signOut = useCallback(async () => {
    await _performSignOut(false);
  }, [_performSignOut]);

  const signOutSoft = useCallback(async () => {
    await _performSignOut(true);
  }, [_performSignOut]);

  const forget = useCallback(async (community: CommunityCode, options?: { force?: boolean }): Promise<void> => {
    if (currentCommunity === community && !options?.force) {
      throw new Error('Cannot forget current community. Sign out first.');
    }

    const forgottenCreds = communityCredentials[community];

    // Delete credentials and auth status for the community
    const newCredentials = { ...communityCredentials };
    delete newCredentials[community];

    const newAuthStatus = { ...communityAuthStatus };
    delete newAuthStatus[community];

    // Update state
    setCommunityCredentials(newCredentials);
    setCommunityAuthStatus(newAuthStatus);

    // Save the updated credentials
    await saveAuthData(newCredentials, undefined);

    // Fire-and-forget so the local removal never waits on the network (the iOS
    // token fetch can pend indefinitely offline). Skipped on force: those
    // credentials are already invalid (e.g. account just deleted).
    if (Platform.OS !== 'web' && !options?.force && forgottenCreds?.AuthToken) {
      unregisterPushToken(community, forgottenCreds.AuthToken);
    }
  }, [currentCommunity, communityCredentials, communityAuthStatus, saveAuthData]);

  const getCredentials = useCallback((community: CommunityCode) => {
    return communityCredentials[community] || null;
  }, [communityCredentials]);

  const switchCommunity = useCallback(async (community: CommunityCode) => {
    // Defensive guard (Sentry GROKIO-APP-70): a stale closure could call us with
    // an empty `communityCredentials` map even after ApiService is already
    // correctly configured for this community (e.g. by `loadAuthData`). In that
    // case, wiping ApiService and persisting `{}` would corrupt auth state.
    // Skip the no-op switch when we have no creds to apply but ApiService is
    // already on the right community.
    if (!communityCredentials[community] && ApiService.getCurrentCommunity() === community) {
      console.warn(
        '[AuthProvider] Skipping switchCommunity: no credentials in state but ApiService already configured for',
        community
      );
      return;
    }

    // Update API service configuration based on stored creds (may be partial on web)
    updateApiServiceCredentials(communityCredentials, community);

    // Mark this community as the current one and persist last used
    setCurrentCommunity(community);
    await saveAuthData(communityCredentials, community);

    // Clear all TanStack Query caches to prevent data leakage across communities
    queryClient.clear();

    // Reset navigation so no tab keeps child screens from the previous
    // community. Lives here so every switch surface is covered —
    // per-navigator calls (router.replace, dismissAll) only reach the
    // focused stack.
    resetAppNavigationTree(navigationContainerRef);

    // Clear the current account status to avoid displaying stale data while switching
    setAccountStatus(null);

    // Run auth check for the selected community, forcing a refresh of account status
    await checkAuthForCommunity(community, { force: true });
  }, [communityCredentials, queryClient, checkAuthForCommunity, saveAuthData, navigationContainerRef]);

  const updateAccountIconUrl = useCallback((iconUrl: string) => {
    setAccountStatus((prev) => (prev ? { ...prev, IconUrl: iconUrl } : prev));
  }, []);

  const updateStoredUsername = useCallback(async (community: CommunityCode, newUsername: string) => {
    const newCredentials = { ...communityCredentials };
    const existing = newCredentials[community];
    newCredentials[community] = {
      UserName: newUsername,
      MemberId: existing?.MemberId,
      AuthToken: existing?.AuthToken,
    };
    setCommunityCredentials(newCredentials);
    await saveAuthData(newCredentials, undefined);
  }, [communityCredentials, saveAuthData]);

  const providerValue = useMemo<AuthContextProps>(() => ({
    isAuthenticated: isCurrentCommunityAuthenticated(),
    signIn,
    signOut,
    signOutSoft,
    forget,
    switchCommunity,
    currentCommunity,
    lastUsedCommunity,
    getCredentials,
    accountStatus,
    isLoading,
    isAuthenticating,
    updateAccountIconUrl,
    updateStoredUsername,
  }), [
    isCurrentCommunityAuthenticated,
    signIn,
    signOut,
    signOutSoft,
    forget,
    switchCommunity,
    currentCommunity,
    lastUsedCommunity,
    getCredentials,
    accountStatus,
    isLoading,
    isAuthenticating,
    updateAccountIconUrl,
    updateStoredUsername,
  ]);

  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
